/**
 * Libellés de l'écran Comptabilité (ARTO-58), français d'abord puis anglais.
 * Ils vivent dans le namespace `console` : `i18n.ts` les reprend sous la clé
 * `comptabilite`.
 *
 * Voix : phrases courtes, « HT » et « TVA » comme dans la facture, un montant
 * non saisi se dit « à saisir », jamais « 0 € ». Aucun nom de partie prenante
 * n'est écrit ici : ils viennent de l'API.
 */
export const comptabiliteFr = {
  titre: 'Comptabilité',
  texteDirection:
    'Les recettes de la plateforme mois par mois, et la répartition automatique du HT entre les parties du partage. La TVA est collectée pour l’État : elle n’entre pas dans la répartition.',
  texteAssocie:
    'Votre part par mois, et le total HT de la plateforme. Les pourcentages et les parts des autres parties restent chez la direction.',
  chargement: 'Chargement de la comptabilité…',
  erreur: 'La comptabilité n’a pas pu être chargée. Réessayez.',
  noteTva:
    'La TVA est collectée pour l’État et n’est pas répartie. Les parts se calculent sur le HT, au centime.',
  noteHt: 'Les parts se calculent sur le HT, au centime.',
  statut: { provisoire: 'Provisoire', cloture: 'Clôturé' },
  apercu: {
    titre: 'Aperçu non enregistré.',
    texte_one:
      '{{count}} service modifié, appliqué à partir de {{mois}}. Les mois clôturés ne bougent pas.',
    texte_other:
      '{{count}} services modifiés, appliqués à partir de {{mois}}. Les mois clôturés ne bougent pas.',
    voir: 'Voir les pourcentages',
  },
  mois: {
    titre: 'Le mois en chiffres',
    navigation: 'Choisir le mois',
    affiche: 'Mois affiché',
    precedent: 'Mois précédent',
    suivant: 'Mois suivant',
    provisoireSuffixe: '{{mois}} (provisoire)',
  },
  tuiles: {
    ht: 'Recettes HT',
    htAide: 'Base de la répartition',
    tva: 'TVA',
    tvaAide: 'Collectée pour l’État, non répartie',
    ttc: 'TTC',
    ttcAide: 'Ce que paient les galeries',
    aTraiter: 'À saisir ou à classer',
    aTraiterFige: 'Mois clôturé, chiffres figés',
    aTraiterDetail: '{{saisir}} à saisir · {{classer}} à classer',
    aTraiterRien: 'Rien ne bloque la clôture sur les montants',
    rien: 'Rien',
    lignes_one: '{{count}} ligne',
    lignes_other: '{{count}} lignes',
  },
  tuilesAssocie: {
    ht: 'HT du mois',
    htAide: 'Tous services confondus',
    maPart: 'Votre part du mois',
    maPartCloture: 'Inscrite à vos versements',
    maPartProvisoire: 'Provisoire, peut encore changer',
    cumul: 'Votre part, mois clôturés',
    cumulAide_one: '{{count}} mois clôturé sur la période',
    cumulAide_other: '{{count}} mois clôturés sur la période',
    statut: 'Statut du mois',
    statutCloture: 'La direction l’a clôturé',
    statutProvisoire: 'La direction le clôture',
  },
  typeInconnu: 'Type de recette inconnu',
  encaissements: {
    titre: 'Encaissements',
    note: 'Une somme reçue, et sa répartition aussitôt calculée',
    texte:
      'Saisissez une somme reçue : son type décide de la répartition, calculée sur le HT selon la grille du mois de son encaissement.',
    formulaire: 'Nouvel encaissement',
    formulaireModif: 'Corriger l’encaissement du {{date}}',
    date: 'Reçu le',
    type: 'Type de recette',
    choisirType: 'Choisir un type',
    montant: 'Montant reçu',
    saisie: 'Le montant est',
    ht: 'HT',
    ttc: 'TTC',
    taux: 'TVA',
    payeur: 'Payeur',
    payeurAide: 'Qui a payé : la direction seule le lit.',
    galerie: 'Galerie',
    sansGalerie: 'Aucune',
    libelle: 'Libellé',
    libelleAide: 'Facultatif : le nom du type, sinon.',
    montantInvalide: 'Montant attendu en euros, par exemple 1 500 ou 1 500,50.',
    tauxInvalide: 'Taux attendu en pourcentage, de 0 à 100.',
    apercu: 'Aperçu de la répartition',
    apercuVide: 'Saisissez un montant et un type : la répartition s’affiche ici.',
    apercuHt: 'HT',
    apercuTva: 'TVA ({{taux}})',
    apercuTtc: 'TTC',
    sansGrille: '{{type}} n’a pas de grille pour {{mois}} : la somme ne sera pas répartie.',
    sansReste: 'Une galerie exclue sans bénéficiaire du reste : la somme ne sera pas répartie.',
    reglerGrille: 'Régler les pourcentages',
    moisCloture: '{{mois}} est clôturé : rouvrez-le pour y enregistrer une somme.',
    objectif: '{{recu}} reçus sur {{objectif}} — reste {{reste}}',
    objectifAtteint: '{{recu}} reçus sur {{objectif}} — objectif atteint',
    objectifApres: 'Après cet encaissement : {{recu}} sur {{objectif}}.',
    objectifDepasse: 'Cet encaissement dépasse l’objectif de {{ecart}}. Il sera accepté.',
    enregistrer: 'Enregistrer l’encaissement',
    enregistrerModif: 'Enregistrer la correction',
    annuler: 'Annuler',
    enregistre: 'Encaissement enregistré : {{montant}} HT.',
    liste: 'Encaissements — {{mois}}',
    vide: 'Aucun encaissement en {{mois}}.',
    nonReparti: 'Non réparti',
    exclueEspaceDeTest:
      'Exclu des comptes (espace de test) : la galerie est un espace de test, cette somme n’est pas répartie.',
    figeeNonDetaillee:
      'Parts figées non détaillées : ce mois a été clôturé avant que chaque encaissement garde sa répartition ; les parts du mois font foi.',
    modifier: 'Corriger',
    modifierAria: 'Corriger l’encaissement du {{date}}, {{montant}}',
    supprimer: 'Supprimer',
    supprimerAria: 'Supprimer l’encaissement du {{date}}, {{montant}}',
    confirmerSuppression: 'Supprimer cet encaissement ?',
    confirmer: 'Supprimer',
    fige: 'Mois clôturé : ses encaissements ne se corrigent plus.',
    saisieTtc: 'saisi TTC',
  },
  types: {
    titre: 'Types de recette',
    note: 'Les cinq services sont fixes. Créez un type pour une recette qui n’en relève pas (une contribution versée en plusieurs fois) : il a sa propre ligne de pourcentages ci-dessus.',
    aucun: 'Aucun type créé.',
    nouveau: 'Nouveau type',
    libelleFr: 'Nom',
    libelleEn: 'Nom en anglais (facultatif)',
    objectif: 'Objectif HT (facultatif)',
    objectifAide: 'Le montant HT à recevoir sur ce type, en euros.',
    creer: 'Créer le type',
    cree: 'Type « {{nom}} » créé : réglez ses pourcentages ci-dessus.',
    actif: 'Actif',
    archive: 'Archivé',
    sansObjectif: '{{recu}} reçus, sans objectif',
    modifier: 'Modifier',
    modifierAria: 'Modifier le type {{nom}}',
    enregistrer: 'Enregistrer',
    annuler: 'Annuler',
    archiver: 'Archiver',
    archiverAria: 'Archiver le type {{nom}}',
    retablir: 'Rétablir',
    retablirAria: 'Rétablir le type {{nom}}',
    supprimer: 'Supprimer',
    supprimerAria: 'Supprimer le type {{nom}}',
    aideArchive: 'Archivé : plus d’encaissement, ses mois passés comptent toujours.',
  },
  services: {
    abonnement: 'Abonnements',
    integration: 'Intégration',
    migration: 'Migration',
    site_galerie: 'Création de site galerie',
    add_on: 'Add-ons',
  },
  servicesCourts: {
    abonnement: 'Abonnements',
    integration: 'Intégration',
    migration: 'Migration',
    site_galerie: 'Site galerie',
    add_on: 'Add-ons',
  },
  flux: {
    titre: 'Des services aux parties',
    noteDirection: 'Largeur proportionnelle au montant HT',
    noteAssocie: 'Votre part, sur le HT de chaque service',
    aria: 'Diagramme de flux : du HT de chaque service vers les parties, {{mois}}. Les montants sont dans le tableau sous le diagramme.',
    invite: 'Survolez ou sélectionnez un flux pour lire son montant.',
    lecture: '{{service}} → {{partie}} : {{montant}} ({{part}} du HT du service)',
    vide: 'Aucune recette ce mois-là.',
    tableau: 'Voir les montants en tableau',
    tableauLegende: 'Flux du HT, de chaque service vers chaque partie',
    colService: 'Service',
    colHt: 'HT',
    total: 'Total',
    maPart: 'Votre part',
    resteHt: 'Reste du HT',
    nonReparti: 'Non réparti',
  },
  historique: {
    titre: 'Douze mois',
    note: 'Sélectionnez un mois pour l’afficher',
    legende: 'Légende des barres',
    provisoire: 'Barre claire : provisoire',
    aria: 'Recettes HT des {{count}} derniers mois, réparties par partie',
    barre: '{{mois}} : HT {{ht}}, {{statut}}',
    maPart: 'Votre part',
    maPartMontant: 'votre part {{montant}}',
    htMois: 'HT du mois',
    htLecture: 'HT {{montant}}',
  },
  grille: {
    titre: 'Pourcentages par service',
    note: 'Réglez chaque partie : {{reste}} prend le reste',
    noteVide: 'Le partage n’a encore aucun bénéficiaire',
    sansBeneficiaire: 'Ajoutez d’abord des bénéficiaires au partage pour répartir les recettes.',
    ouvrirPartage: 'Ouvrir le partage',
    resteChoix: 'Reçoit le reste',
    aria: 'Pourcentages par service',
    colService: 'Service',
    colReste: '{{nom}} (reste)',
    sansGrille: 'Sans grille',
    aucuneVersion: 'Aucune version',
    versions_one: '{{count}} version',
    versions_other: '{{count}} versions',
    depuis: 'À partir de {{mois}}',
    etat: { enVigueur: 'En vigueur', aVenir: 'À venir', remplacee: 'Remplacée' },
    figee_one: 'a réparti {{count}} mois clôturé',
    figee_other: 'a réparti {{count}} mois clôturés',
    curseur: '{{partie}}, {{service}}',
    champ: '{{partie}}, {{service}}, en pourcentage',
    plafond: 'Le total ne peut pas dépasser 100 % : {{service}} est ramené au maximum possible.',
    depuisChamp: 'Applicable à partir de',
    departCourant: '{{mois}} (mois courant)',
    departACloturer: '{{mois}} (mois à clôturer)',
    departSimple: '{{mois}}',
    enregistrer: 'Enregistrer la version',
    annuler: 'Annuler les changements',
    enregistree_one: 'Version enregistrée pour {{count}} service, applicable à partir de {{mois}}.',
    enregistree_other:
      'Version enregistrée pour {{count}} services, applicable à partir de {{mois}}.',
  },
  exclusions: {
    titre: 'Galeries exclues',
    note: 'Sur les recettes d’une galerie exclue, la part du bénéficiaire va à {{reste}} ; les autres gardent la leur.',
    sansReste:
      'Aucun bénéficiaire ne reçoit encore le reste : choisissez-le ci-dessus, sinon les services d’une galerie exclue ne sont pas répartis.',
    aria: 'Galeries exclues de la part de {{partie}}',
    aucune: 'Aucune',
    galerieDepuis: '{{galerie}}, à partir de {{mois}}',
    galerieInconnue: 'Galerie inconnue',
    galerie: 'Galerie à exclure de la part de {{partie}}',
    depuis: 'Mois de départ de l’exclusion pour {{partie}}',
    choisirGalerie: 'Choisir une galerie',
    exclure: 'Exclure',
    retirer: 'Retirer',
    retirerAria: 'Retirer l’exclusion de {{galerie}} pour {{partie}}',
  },
  cloture: {
    titre: 'Clôture du mois',
    texte: 'Clôturer fige les chiffres et inscrit le dû de chaque partie dans ses versements.',
    texteCloture:
      'Mois clôturé le {{date}}. Les chiffres sont figés et le dû de chaque partie est inscrit à ses versements.',
    aria: 'Ce qui reste à régler avant de clôturer {{mois}}',
    enOrdre: 'En ordre',
    aRegler: 'À régler',
    termine: 'Mois terminé',
    termineOui: 'oui',
    termineNon: 'il se clôture à partir du premier jour de {{mois}}',
    montants: 'Montants à saisir',
    recettes: 'Recettes à classer',
    grilles: 'Services sans grille',
    aucun: 'aucun',
    aucune: 'aucune',
    aSaisir_one: '{{count}} montant à renseigner',
    aSaisir_other: '{{count}} montants à renseigner',
    aClasser_one: '{{count}} recette sans service',
    aClasser_other: '{{count}} recettes sans service',
    sansGrille_one: '{{count}} service à régler',
    sansGrille_other: '{{count}} services à régler',
    sansGrilleListe: 'Services qui ont des recettes sans grille : {{services}}.',
    ouvrirAbonnements: 'Ouvrir les abonnements',
    ouvrirRevenus: 'Ouvrir les revenus ponctuels',
    classer: 'Classer les revenus',
    regler: 'Régler les pourcentages',
    brouillon: 'Pourcentages modifiés',
    brouillonDetail: 'à enregistrer ou à annuler',
    cloturer: 'Clôturer {{mois}}',
    reste_one: 'Il reste {{count}} point à régler avant de clôturer.',
    reste_other: 'Il reste {{count}} points à régler avant de clôturer.',
    pret: 'Tout est en ordre : vous pouvez clôturer.',
    confirmation: 'Confirmation',
    clotureTitre: 'Clôturer {{mois}} ?',
    clotureTexte: 'Les chiffres seront figés. Le dû inscrit aux versements :',
    clotureRouvrir: 'Clôturer, comme rouvrir, demande une vérification récente du second facteur.',
    confirmer: 'Confirmer la clôture',
    annuler: 'Annuler',
    rouvrir: 'Rouvrir ce mois',
    rouvrirTitre: 'Rouvrir {{mois}} ?',
    rouvrirTexte:
      'Le mois redevient provisoire et ses chiffres peuvent changer. Les versements déjà inscrits ne sont pas modifiés.',
    rouvrirConfirmer: 'Rouvrir le mois',
    fait: {
      cloture: '{{mois}} est clôturé. Le dû de chaque partie est inscrit à ses versements.',
      reouverture: '{{mois}} est rouvert : il est de nouveau provisoire.',
    },
  },
};

export const comptabiliteEn: typeof comptabiliteFr = {
  titre: 'Accounting',
  texteDirection:
    'Platform revenue month by month, and the automatic split of the net amount between the parties of the revenue share. VAT is collected for the State: it is not part of the split.',
  texteAssocie:
    'Your share by month, and the platform’s total excl. tax. The other parties’ percentages and shares stay with the management.',
  chargement: 'Loading accounting…',
  erreur: 'Accounting could not be loaded. Try again.',
  noteTva:
    'VAT is collected for the State and is not split. Shares are calculated on the net amount, to the cent.',
  noteHt: 'Shares are calculated on the net amount, to the cent.',
  statut: { provisoire: 'Provisional', cloture: 'Closed' },
  apercu: {
    titre: 'Unsaved preview.',
    texte_one: '{{count}} service changed, applied from {{mois}}. Closed months do not move.',
    texte_other: '{{count}} services changed, applied from {{mois}}. Closed months do not move.',
    voir: 'See the percentages',
  },
  mois: {
    titre: 'The month in figures',
    navigation: 'Choose the month',
    affiche: 'Month shown',
    precedent: 'Previous month',
    suivant: 'Next month',
    provisoireSuffixe: '{{mois}} (provisional)',
  },
  tuiles: {
    ht: 'Revenue excl. tax',
    htAide: 'Basis of the split',
    tva: 'VAT',
    tvaAide: 'Collected for the State, not split',
    ttc: 'Incl. tax',
    ttcAide: 'What the galleries pay',
    aTraiter: 'To enter or classify',
    aTraiterFige: 'Month closed, figures frozen',
    aTraiterDetail: '{{saisir}} to enter · {{classer}} to classify',
    aTraiterRien: 'Nothing blocks closing on the amounts',
    rien: 'Nothing',
    lignes_one: '{{count}} line',
    lignes_other: '{{count}} lines',
  },
  tuilesAssocie: {
    ht: 'Month, excl. tax',
    htAide: 'All services together',
    maPart: 'Your share this month',
    maPartCloture: 'Entered in your payouts',
    maPartProvisoire: 'Provisional, may still change',
    cumul: 'Your share, closed months',
    cumulAide_one: '{{count}} closed month in the period',
    cumulAide_other: '{{count}} closed months in the period',
    statut: 'Month status',
    statutCloture: 'The management closed it',
    statutProvisoire: 'The management closes it',
  },
  typeInconnu: 'Unknown revenue type',
  encaissements: {
    titre: 'Payments received',
    note: 'A sum received, and its split computed at once',
    texte:
      'Enter a sum received: its type decides the split, computed on the net amount with the grid of the month it was received.',
    formulaire: 'New payment',
    formulaireModif: 'Correct the payment of {{date}}',
    date: 'Received on',
    type: 'Revenue type',
    choisirType: 'Choose a type',
    montant: 'Amount received',
    saisie: 'The amount is',
    ht: 'Net',
    ttc: 'Gross',
    taux: 'VAT',
    payeur: 'Payer',
    payeurAide: 'Who paid: only management reads it.',
    galerie: 'Gallery',
    sansGalerie: 'None',
    libelle: 'Label',
    libelleAide: 'Optional: the type name otherwise.',
    montantInvalide: 'Amount expected in euros, for example 1500 or 1500.50.',
    tauxInvalide: 'Rate expected as a percentage, from 0 to 100.',
    apercu: 'Split preview',
    apercuVide: 'Enter an amount and a type: the split shows here.',
    apercuHt: 'Net',
    apercuTva: 'VAT ({{taux}})',
    apercuTtc: 'Gross',
    sansGrille: '{{type}} has no grid for {{mois}}: the sum will not be split.',
    sansReste:
      'An excluded gallery and no beneficiary of the remainder: the sum will not be split.',
    reglerGrille: 'Set the percentages',
    moisCloture: '{{mois}} is closed: reopen it to record a sum.',
    objectif: '{{recu}} received of {{objectif}} — {{reste}} to go',
    objectifAtteint: '{{recu}} received of {{objectif}} — target reached',
    objectifApres: 'After this payment: {{recu}} of {{objectif}}.',
    objectifDepasse: 'This payment exceeds the target by {{ecart}}. It will be accepted.',
    enregistrer: 'Record the payment',
    enregistrerModif: 'Save the correction',
    annuler: 'Cancel',
    enregistre: 'Payment recorded: {{montant}} net.',
    liste: 'Payments — {{mois}}',
    vide: 'No payment in {{mois}}.',
    nonReparti: 'Not split',
    exclueEspaceDeTest:
      'Excluded from the accounts (test space): the gallery is a test space, this sum is not split.',
    figeeNonDetaillee:
      'Frozen shares not itemised: this month was closed before each payment kept its split; the month’s shares apply.',
    modifier: 'Correct',
    modifierAria: 'Correct the payment of {{date}}, {{montant}}',
    supprimer: 'Delete',
    supprimerAria: 'Delete the payment of {{date}}, {{montant}}',
    confirmerSuppression: 'Delete this payment?',
    confirmer: 'Delete',
    fige: 'Closed month: its payments can no longer be corrected.',
    saisieTtc: 'entered gross',
  },
  types: {
    titre: 'Revenue types',
    note: 'The five services are fixed. Create a type for revenue that fits none of them (a contribution paid in instalments): it gets its own row of percentages above.',
    aucun: 'No type created.',
    nouveau: 'New type',
    libelleFr: 'Name (French)',
    libelleEn: 'Name in English (optional)',
    objectif: 'Net target (optional)',
    objectifAide: 'The net amount to receive on this type, in euros.',
    creer: 'Create the type',
    cree: 'Type “{{nom}}” created: set its percentages above.',
    actif: 'Active',
    archive: 'Archived',
    sansObjectif: '{{recu}} received, no target',
    modifier: 'Edit',
    modifierAria: 'Edit the type {{nom}}',
    enregistrer: 'Save',
    annuler: 'Cancel',
    archiver: 'Archive',
    archiverAria: 'Archive the type {{nom}}',
    retablir: 'Restore',
    retablirAria: 'Restore the type {{nom}}',
    supprimer: 'Delete',
    supprimerAria: 'Delete the type {{nom}}',
    aideArchive: 'Archived: no more payments, its past months still count.',
  },
  services: {
    abonnement: 'Subscriptions',
    integration: 'Onboarding',
    migration: 'Migration',
    site_galerie: 'Gallery website creation',
    add_on: 'Add-ons',
  },
  servicesCourts: {
    abonnement: 'Subscriptions',
    integration: 'Onboarding',
    migration: 'Migration',
    site_galerie: 'Gallery site',
    add_on: 'Add-ons',
  },
  flux: {
    titre: 'From services to parties',
    noteDirection: 'Width proportional to the net amount',
    noteAssocie: 'Your share, on each service’s net amount',
    aria: 'Flow diagram: from each service’s net amount to the parties, {{mois}}. The amounts are in the table under the diagram.',
    invite: 'Hover or select a flow to read its amount.',
    lecture: '{{service}} → {{partie}}: {{montant}} ({{part}} of the service’s net amount)',
    vide: 'No revenue that month.',
    tableau: 'See the amounts as a table',
    tableauLegende: 'Net amount flow, from each service to each party',
    colService: 'Service',
    colHt: 'Excl. tax',
    total: 'Total',
    maPart: 'Your share',
    resteHt: 'Rest of the net amount',
    nonReparti: 'Not split',
  },
  historique: {
    titre: 'Twelve months',
    note: 'Select a month to show it',
    legende: 'Bar legend',
    provisoire: 'Light bar: provisional',
    aria: 'Revenue excl. tax for the last {{count}} months, split by party',
    barre: '{{mois}}: {{ht}} excl. tax, {{statut}}',
    maPart: 'Your share',
    maPartMontant: 'your share {{montant}}',
    htMois: 'Month, excl. tax',
    htLecture: '{{montant}} excl. tax',
  },
  grille: {
    titre: 'Percentages by service',
    note: 'Set each party: {{reste}} takes the rest',
    noteVide: 'The revenue share has no beneficiary yet',
    sansBeneficiaire: 'First add beneficiaries to the revenue share to split the revenue.',
    ouvrirPartage: 'Open the revenue share',
    resteChoix: 'Receives the rest',
    aria: 'Percentages by service',
    colService: 'Service',
    colReste: '{{nom}} (rest)',
    sansGrille: 'No split',
    aucuneVersion: 'No version',
    versions_one: '{{count}} version',
    versions_other: '{{count}} versions',
    depuis: 'From {{mois}}',
    etat: { enVigueur: 'In force', aVenir: 'Upcoming', remplacee: 'Replaced' },
    figee_one: 'split {{count}} closed month',
    figee_other: 'split {{count}} closed months',
    curseur: '{{partie}}, {{service}}',
    champ: '{{partie}}, {{service}}, as a percentage',
    plafond: 'The total cannot exceed 100%: {{service}} is brought down to the maximum possible.',
    depuisChamp: 'Applies from',
    departCourant: '{{mois}} (current month)',
    departACloturer: '{{mois}} (month to close)',
    departSimple: '{{mois}}',
    enregistrer: 'Save the version',
    annuler: 'Discard changes',
    enregistree_one: 'Version saved for {{count}} service, applying from {{mois}}.',
    enregistree_other: 'Version saved for {{count}} services, applying from {{mois}}.',
  },
  exclusions: {
    titre: 'Excluded galleries',
    note: 'On revenue from an excluded gallery, the beneficiary’s share goes to {{reste}}; the others keep theirs.',
    sansReste:
      'No beneficiary receives the rest yet: choose one above, otherwise the services of an excluded gallery are not split.',
    aria: 'Galleries excluded from {{partie}}’s share',
    aucune: 'None',
    galerieDepuis: '{{galerie}}, starting {{mois}}',
    galerieInconnue: 'Unknown gallery',
    galerie: 'Gallery to exclude from {{partie}}’s share',
    depuis: 'Starting month of the exclusion for {{partie}}',
    choisirGalerie: 'Choose a gallery',
    exclure: 'Exclude',
    retirer: 'Remove',
    retirerAria: 'Remove the exclusion of {{galerie}} for {{partie}}',
  },
  cloture: {
    titre: 'Month-end closing',
    texte: 'Closing freezes the figures and enters each party’s amount due in their payouts.',
    texteCloture:
      'Month closed on {{date}}. The figures are frozen and each party’s amount due is entered in their payouts.',
    aria: 'What is left to settle before closing {{mois}}',
    enOrdre: 'In order',
    aRegler: 'To settle',
    termine: 'Month is over',
    termineOui: 'yes',
    termineNon: 'it can be closed from the first day of {{mois}}',
    montants: 'Amounts to enter',
    recettes: 'Revenue to classify',
    grilles: 'Services without a split',
    aucun: 'none',
    aucune: 'none',
    aSaisir_one: '{{count}} amount to enter',
    aSaisir_other: '{{count}} amounts to enter',
    aClasser_one: '{{count}} revenue without a service',
    aClasser_other: '{{count}} revenues without a service',
    sansGrille_one: '{{count}} service to set',
    sansGrille_other: '{{count}} services to set',
    sansGrilleListe: 'Services that have revenue but no split: {{services}}.',
    ouvrirAbonnements: 'Open subscriptions',
    ouvrirRevenus: 'Open one-off revenue',
    classer: 'Classify the revenue',
    regler: 'Set the percentages',
    brouillon: 'Percentages changed',
    brouillonDetail: 'to save or discard',
    cloturer: 'Close {{mois}}',
    reste_one: '{{count}} point left to settle before closing.',
    reste_other: '{{count}} points left to settle before closing.',
    pret: 'Everything is in order: you can close.',
    confirmation: 'Confirmation',
    clotureTitre: 'Close {{mois}}?',
    clotureTexte: 'The figures will be frozen. The amount due entered in the payouts:',
    clotureRouvrir: 'Closing, like reopening, requires a recent second-factor check.',
    confirmer: 'Confirm the closing',
    annuler: 'Cancel',
    rouvrir: 'Reopen this month',
    rouvrirTitre: 'Reopen {{mois}}?',
    rouvrirTexte:
      'The month becomes provisional again and its figures may change. Payouts already entered are not changed.',
    rouvrirConfirmer: 'Reopen the month',
    fait: {
      cloture: '{{mois}} is closed. Each party’s amount due is entered in their payouts.',
      reouverture: '{{mois}} is reopened: it is provisional again.',
    },
  },
};
